import { useState } from 'react'
import { Link, useRouterState } from '@tanstack/react-router'
import { Lock, PanelLeftClose, PanelLeftOpen } from 'lucide-react'
import type { NavEntry } from '../../features/feature.ts'
import { useT } from '../../lib/i18n.tsx'
import { Kbd } from '../../kit/Kbd.tsx'
import { Tooltip } from '../../kit/Tooltip.tsx'
import { cn } from '../../kit/util.ts'
import { isActive, NAV_GROUPS, useNavEntries } from '../nav.ts'
import { BrandMark, BrandName } from './Brand.tsx'

const KEY = 'nw:rail'

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(KEY) === 'collapsed'
  } catch {
    return false
  }
}

function RailItem({ entry, collapsed, pathname }: { entry: NavEntry; collapsed: boolean; pathname: string }) {
  const t = useT()
  const active = isActive(pathname, entry.to)
  const label = t(entry.label)
  const keys = entry.key ? ['g', entry.key] : undefined
  const Icon = entry.icon
  const link = (
    <Link
      to={entry.to}
      aria-label={collapsed ? label : undefined}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'group relative flex h-8 items-center gap-2.5 rounded-md text-sm font-medium transition',
        collapsed ? 'w-9 justify-center' : 'px-2.5',
        active ? 'bg-raised text-ink shadow-raised ring-1 ring-line ring-inset' : 'text-ink-muted hover:bg-hover hover:text-ink',
      )}
    >
      {active && <span aria-hidden className="absolute inset-y-2 -left-2 w-0.5 rounded-full bg-indicator" />}
      <Icon size={16} strokeWidth={1.75} aria-hidden className={cn('shrink-0', active ? 'text-accent-text' : 'text-ink-faint group-hover:text-ink-muted')} />
      {!collapsed && <span className="min-w-0 flex-1 truncate">{label}</span>}
      {!collapsed && entry.to === '/network' && (
        <>
          <Lock size={12} strokeWidth={2} aria-hidden className="shrink-0 text-private" />
          <span className="sr-only">, {t('nav.private')}</span>
        </>
      )}
      {!collapsed && keys && <Kbd keys={keys} tone="plain" className="opacity-0 transition group-hover:opacity-100 group-focus-visible:opacity-100" />}
    </Link>
  )
  return collapsed ? (
    <Tooltip content={label} kbd={keys} side="right">
      {link}
    </Tooltip>
  ) : (
    link
  )
}

/** Left rail with icons and labels in groups, collapsible; hidden on phones. */
export function Rail() {
  const t = useT()
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const [collapsed, setCollapsed] = useState(readCollapsed)
  const toggle = () => {
    setCollapsed((c) => {
      try {
        localStorage.setItem(KEY, c ? 'open' : 'collapsed')
      } catch {
        /* storage unavailable */
      }
      return !c
    })
  }
  const navEntries = useNavEntries()
  const system = navEntries.filter((e) => e.group === 'system')
  const toggleLabel = collapsed ? t('nav.expandRail') : t('nav.collapseRail')

  return (
    <nav
      aria-label={t('nav.main')}
      className={cn(
        'hidden shrink-0 flex-col py-3 transition-all duration-base ease-out md:flex',
        collapsed ? 'w-rail-collapsed items-center px-2' : 'w-rail px-3',
      )}
    >
      <div className={cn('mb-5 flex h-9 items-center gap-2.5', collapsed ? 'justify-center' : 'px-1.5')}>
        <BrandMark />
        {!collapsed && <BrandName />}
      </div>

      <div className={cn('flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto scrollbar-none', collapsed ? '-mx-2 px-2' : '-mx-3 px-3')}>
        {NAV_GROUPS.map((g) => {
          const items = navEntries.filter((e) => e.group === g)
          if (items.length === 0) return null
          const heading = g === 'network' ? t('nav.group.network') : g === 'later' ? t('nav.group.later') : null
          return (
            <div key={g} className="flex flex-col gap-0.5">
              {heading && !collapsed && <div className="label-mono px-2.5 pb-1.5 text-ink-faint">{heading}</div>}
              {heading && collapsed && <div aria-hidden className="mx-auto mb-1.5 h-px w-5 bg-line-strong" />}
              {items.map((e) => (
                <RailItem key={e.to} entry={e} collapsed={collapsed} pathname={pathname} />
              ))}
            </div>
          )
        })}
      </div>

      <div className="mt-3 flex flex-col gap-0.5 border-t border-line pt-3">
        {system.map((e) => (
          <RailItem key={e.to} entry={e} collapsed={collapsed} pathname={pathname} />
        ))}
        <Tooltip content={toggleLabel} side="right">
          <button
            type="button"
            onClick={toggle}
            aria-label={toggleLabel}
            aria-expanded={!collapsed}
            className={cn(
              'mt-1 flex h-8 items-center gap-2.5 rounded-md text-sm text-ink-faint transition hover:bg-hover hover:text-ink',
              collapsed ? 'w-9 justify-center' : 'px-2.5',
            )}
          >
            {collapsed ? <PanelLeftOpen size={16} strokeWidth={1.75} aria-hidden /> : <PanelLeftClose size={16} strokeWidth={1.75} aria-hidden />}
            {!collapsed && <span>{t('nav.collapse')}</span>}
          </button>
        </Tooltip>
      </div>
    </nav>
  )
}
